import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { faqs, processSteps } from '../mock';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import SEO from '../components/SEO';

const FAQ = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // SEO meta tags
  useEffect(() => {
    document.title = 'FAQ Béton Décoratif - Questions Fréquentes | DumaExpertBéton';
    const descMeta = document.querySelector('meta[name="description"]');
    if (descMeta) descMeta.setAttribute('content', 'Réponses à toutes vos questions sur le béton imprimé, désactivé et décoratif. Prix, durabilité, entretien, délais. Expert PACA depuis 2014.');
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = 'https://duma-expert-beton.fr/faq';
    return () => { document.title = 'DumaExpertBéton - Expert en Béton Imprimé et Décoratif | Orange, Vaucluse'; };
  }, []);

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };
  
  // Translated process steps
  const translatedSteps = [
    {
      id: 1,
      title: t('processSteps.step1Title'),
      description: t('processSteps.step1Desc'),
      icon: 'MessageSquare'
    },
    {
      id: 2,
      title: t('processSteps.step2Title'),
      description: t('processSteps.step2Desc'),
      icon: 'FileText'
    },
    {
      id: 3,
      title: t('processSteps.step3Title'),
      description: t('processSteps.step3Desc'),
      icon: 'Calendar'
    },
    {
      id: 4,
      title: t('processSteps.step4Title'),
      description: t('processSteps.step4Desc'),
      icon: 'Wrench'
    },
    {
      id: 5,
      title: t('processSteps.step5Title'),
      description: t('processSteps.step5Desc'),
      icon: 'CheckCircle'
    },
    {
      id: 6,
      title: t('processSteps.step6Title'),
      description: t('processSteps.step6Desc'),
      icon: 'HeadphonesIcon'
    }
  ];
  // Schema.org FAQPage for LLMs
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(faqSchema);
    script.id = 'faq-schema';
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('faq-schema');
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      
      {/* Header */}
      <div className="bg-slate-900 text-white py-8 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto">
          <div className="flex justify-between items-start mb-4">
            <button onClick={handleBack} className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800/50 hover:bg-slate-700/50 rounded-lg border border-slate-700 transition-all duration-200 cursor-pointer">
              <ArrowLeft className="w-4 h-4" />
              <span className="font-medium">{t('common.back')}</span>
            </button>
            <LanguageSwitcher />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mt-4">{t('faq.title')}</h1>
          <p className="text-xl text-slate-300 mt-4">{t('faq.subtitle')}</p>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Process Steps */}
        <section className="mb-16">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-[#C97249] text-white hover:bg-[#B8624A]">{t('faq.ourProcess')}</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              {t('faq.howWeWork')}
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              {t('faq.processDescription')}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {translatedSteps.map((step) => (
              <Card key={step.id} className="relative overflow-hidden bg-slate-800 border-slate-700">
                <div className="absolute top-4 right-4 w-12 h-12 bg-[#C97249] text-white rounded-full flex items-center justify-center font-bold text-lg">
                  {step.id}
                </div>
                <CardHeader>
                  <CardTitle className="text-xl pr-16 text-white">{step.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-300">{step.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* FAQ Accordion */}
        <section>
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-[#C97249] text-white hover:bg-[#B8624A]">{t('faq.faqSection')}</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              {t('faq.answersTitle')}
            </h2>
          </div>
          <Card className="max-w-4xl mx-auto bg-slate-800 border-slate-700">
            <CardContent className="p-6">
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq) => (
                  <AccordionItem key={faq.id} value={`item-${faq.id}`} className="border-slate-700">
                    <AccordionTrigger className="text-left text-lg font-semibold text-white hover:text-[#C97249]">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-300 text-base leading-relaxed">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        </section>

        {/* CTA */}
        <section className="mt-16 text-center">
          <Card className="bg-gradient-to-br from-slate-800 to-slate-700 text-white border-slate-600">
            <CardContent className="p-12">
              <h3 className="text-3xl font-bold mb-4">{t('faq.notFoundTitle')}</h3>
              <p className="text-xl text-slate-300 mb-8">
                {t('faq.notFoundMessage')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <button className="px-8 py-3 bg-[#C97249] text-white rounded-lg font-semibold hover:bg-[#B8624A] transition-colors">
                    {t('faq.contactUs')}
                  </button>
                </Link>
                <Link to="/rdv">
                  <button className="px-8 py-3 border-2 border-[#C97249] text-white rounded-lg font-semibold hover:bg-[#C97249]/20 transition-colors">
                    {t('faq.bookAppointment')}
                  </button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
};

export default FAQ;